import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { OpsAuthProvider, useOpsAuth } from '@/ops/OpsAuthContext';
import { NotificationsProvider } from '@/contexts/NotificationsContext';
import AppShell from '@/components/AppShell';
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import GoogleCallbackPage from '@/pages/GoogleCallbackPage';
import MicrosoftCallbackPage from '@/pages/MicrosoftCallbackPage';
import DashboardPage from '@/pages/DashboardPage';
import WorkspacesPage from '@/pages/WorkspacesPage';
import NewActionPage from '@/pages/NewActionPage';
import DraftsPage from '@/pages/DraftsPage';
import ActionCardPage from '@/pages/ActionCardPage';
import ActionCardsPage from '@/pages/ActionCardsPage';
import TasksPage from '@/pages/TasksPage';
import ContactsPage from '@/pages/ContactsPage';
import ContactDetailPage from '@/pages/ContactDetailPage';
import SettingsPage from '@/pages/SettingsPage';
import SharedWithMePage from '@/pages/SharedWithMePage';
import MeetingsPage from '@/pages/MeetingsPage';
import SharedActionCardPage from '@/pages/SharedActionCardPage';
import SharedCardInAppPage from '@/pages/SharedCardInAppPage';
import FilesPage from '@/pages/FilesPage';
import WorkspacePage from '@/pages/WorkspacePage';
import WorkspaceScopedView from '@/pages/WorkspaceScopedView';
import PeopleTeamsPage from '@/pages/PeopleTeamsPage';
import PrivacyPage from '@/pages/PrivacyPage';
import TermsPage from '@/pages/TermsPage';
import AboutPage from '@/pages/AboutPage';
import PricingPage from '@/pages/PricingPage';
import OnboardingPage from '@/pages/OnboardingPage';
import LandingPage from '@/pages/LandingPage';
import OpsShell from '@/ops/OpsShell';
import OpsLoginPage from '@/ops/pages/OpsLoginPage';
import OpsDashboardPage from '@/ops/pages/OpsDashboardPage';
import OpsUsersPage from '@/ops/pages/OpsUsersPage';
import OpsUserDetailPage from '@/ops/pages/OpsUserDetailPage';
import OpsLogsPage from '@/ops/pages/OpsLogsPage';
import OpsAnalyticsPage from '@/ops/pages/OpsAnalyticsPage';
import OpsSystemPage from '@/ops/pages/OpsSystemPage';
import OpsSetupPage from '@/ops/pages/OpsSetupPage';
import ConsentBanner from '@/components/ConsentBanner';
import { usePageTracking } from '@/hooks/usePageTracking';
import '@/App.css';


// Fires GA4 page_view on route change (must be inside Router).
function AnalyticsTracker() {
  usePageTracking();
  return null;
}


// Onboarding route — must be logged in but allows onboarding_completed=false
function ProtectedOnboarding({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}


function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }
  // Unauthenticated: show landing page at root, redirect to login elsewhere
  if (!user) {
    if (location.pathname === '/') return <LandingPage />;
    return <Navigate to="/login" replace />;
  }
  // New users go to onboarding
  if (user.onboarding_completed === false) return <Navigate to="/onboarding" replace />;
  return children;
}

function AuthRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
}

// Ops guard: must be logged in as ops user
function OpsRoute({ children }) {
  const { opsUser, loading } = useOpsAuth();
  if (loading) return null;
  if (!opsUser) return <Navigate to="/ops/login" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<AuthRoute><LoginPage /></AuthRoute>} />
      <Route path="/register" element={<AuthRoute><RegisterPage /></AuthRoute>} />
      <Route path="/auth/google" element={<GoogleCallbackPage />} />
      <Route path="/auth/microsoft" element={<MicrosoftCallbackPage />} />
      <Route path="/landing" element={<Navigate to="/" replace />} />
      <Route path="/a/:token" element={<SharedActionCardPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/onboarding" element={<ProtectedOnboarding><OnboardingPage /></ProtectedOnboarding>} />

      {/* /ops — separate auth, separate shell, no user data access */}
      <Route path="/ops/login" element={<OpsLoginPage />} />
      <Route path="/ops/setup" element={<OpsSetupPage />} />
      <Route path="/ops" element={<OpsRoute><OpsShell /></OpsRoute>}>
        <Route index element={<Navigate to="/ops/dashboard" replace />} />
        <Route path="dashboard" element={<OpsDashboardPage />} />
        <Route path="analytics" element={<OpsAnalyticsPage />} />
        <Route path="users" element={<OpsUsersPage />} />
        <Route path="users/:userId" element={<OpsUserDetailPage />} />
        <Route path="system" element={<OpsSystemPage />} />
        <Route path="logs" element={<OpsLogsPage />} />
      </Route>

      <Route path="/" element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
        <Route index element={<DashboardPage />} />
        <Route path="capture" element={<NewActionPage />} />
        <Route path="drafts" element={<DraftsPage />} />
        <Route path="action-cards/:id" element={<ActionCardPage />} />
        <Route path="workspaces" element={<WorkspacesPage />} />
        <Route path="action-cards" element={<ActionCardsPage />} />
        <Route path="tasks" element={<TasksPage />} />
        <Route path="files" element={<FilesPage />} />
        <Route path="meetings" element={<MeetingsPage />} />
        <Route path="workspace" element={<WorkspacePage />} />
        <Route path="w/:teamId" element={<WorkspaceScopedView />} />
        <Route path="teams" element={<PeopleTeamsPage />} />
        <Route path="contacts" element={<ContactsPage />} />
        <Route path="contacts/:id" element={<ContactDetailPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="shared" element={<SharedWithMePage />} />
        <Route path="shared/:token" element={<SharedCardInAppPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <OpsAuthProvider>
            <NotificationsProvider>
              <AnalyticsTracker />
              <AppRoutes />
              <Toaster position="top-right" richColors />
              <ConsentBanner />
            </NotificationsProvider>
          </OpsAuthProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
